import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import useApi from '../hooks/useApi';
import Header from '../components/Header';

// CountUp Animation Component
const CountUpAnimation = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const updateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const progressPercentage = Math.min(progress / (duration * 1000), 1);
      
      // Easing function for smoother animation
      const easeOutQuad = t => t * (2 - t);
      const easedProgress = easeOutQuad(progressPercentage);
      
      setCount(Math.floor(easedProgress * end));
      
      if (progressPercentage < 1) {
        animationFrame = requestAnimationFrame(updateCount);
      }
    };
    
    animationFrame = requestAnimationFrame(updateCount);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  
  // Format the number with commas
  const formattedCount = count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return <span>{formattedCount}{suffix}</span>;
};

const CandidateHomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Fetch real jobs from API
  const { data: jobsData, loading: jobsLoading } = useApi('jobs');
  
  // API wraps response in {success: true, data: {jobs: [...]}}
  const jobs = jobsData?.data?.jobs || jobsData?.jobs || [];
  
  // Filter for featured/active jobs (take first 6)
  const featuredJobs = jobs.filter(job => job.status === 'Active').slice(0, 6);

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Build search parameters
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    if (location.trim()) {
      params.append('location', location.trim());
    }
    
    // Navigate to jobs page with search parameters
    const searchString = params.toString();
    navigate(searchString ? `/jobs?${searchString}` : '/jobs');
  };
  
  const handleJobClick = (jobId) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/auth/candidate', { state: { from: `/jobs/${jobId}` } });
    } else {
      // Navigate to job details
      navigate(`/jobs/${jobId}`);
    }
  };
  
  const handleApplyNow = (e, jobId) => {
    e.stopPropagation(); // Prevent card click event
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/auth/candidate', { state: { from: `/candidate/jobs/${jobId}`, action: 'apply' } });
    } else {
      // Navigate to job detail page
      navigate(`/candidate/jobs/${jobId}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      <main className="flex-1 flex flex-col">
        <section className="w-full bg-gray-100 py-16 px-4 sm:px-6 lg:px-16 relative overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto text-center">
            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-5xl sm:text-6xl md:text-7xl font-black text-black leading-tight mb-4"
            >
              Get 
              <span className="inline-block relative px-1">
                <span className="relative z-10">O</span>
                <span className="absolute inset-0 bg-yellow-400 rounded-full transform scale-105 -z-0"></span>
              </span>ut of the J
              <span className="inline-block relative px-1">
                <span className="relative z-10">o</span>
                <span className="absolute inset-0 bg-yellow-400 rounded-full transform scale-105 -z-0"></span>
              </span>b Finding Loop
            </motion.h1>
            
            {/* Subheading with Counter Animation */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-xl text-black mb-12"
            >
              <CountUpAnimation end={500000} suffix="+" /> jobs for you to explore
            </motion.p>
            
            {/* Search Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="relative max-w-5xl mx-auto"
            >
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row items-stretch bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Skills/Designations/Companies Input */}
                <div className="flex items-center px-4 py-3 md:py-0 flex-1 border-b md:border-b-0 md:border-r border-gray-200">
                  <Search className="h-5 w-5 text-gray-400 mr-2" />
                  <input
                    type="text"
                    placeholder="Enter skills / designations / companies"
                    className="w-full focus:outline-none text-black"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                {/* Location Input */}
                <div className="flex items-center px-4 py-3 md:py-0 flex-1 border-b md:border-b-0 md:border-r border-gray-200">
                  <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                  <input
                    type="text"
                    placeholder="Enter location"
                    className="w-full focus:outline-none text-black"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
                
                {/* Search Button */}
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-900 text-white font-bold px-8 py-4 transition-colors"
                >
                  Search
                </button>
              </form>
            </motion.div>
          </div>
        </section>

        {/* Featured Jobs */}
        <section className="py-16 px-4 sm:px-6 lg:px-16 bg-gray-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-8">Featured Jobs</h2>
            {jobsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading jobs...</p>
              </div>
            ) : featuredJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-600 text-lg">No jobs available at the moment</p>
                <p className="text-gray-500 text-sm mt-2">Check back soon for new opportunities!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredJobs.map((job, index) => {
                  // Get company initials for logo
                  const companyInitials = job.companyName
                    ? job.companyName.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase()
                    : 'CO';
                  
                  // Random logo colors
                  const logoColors = [
                    'bg-blue-100 text-blue-600',
                    'bg-green-100 text-green-600',
                    'bg-purple-100 text-purple-600',
                    'bg-red-100 text-red-600',
                    'bg-yellow-100 text-yellow-600',
                    'bg-indigo-100 text-indigo-600'
                  ];
                  const logoColor = logoColors[index % logoColors.length];
                  
                  // Calculate days since posted
                  const daysAgo = Math.floor((new Date() - new Date(job.createdAt)) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                      onClick={() => handleJobClick(job.id)}
                      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer hover:border-black"
                    >
                      <div className="flex items-start justify-between relative">
                        <div>
                          <h3 className="text-xl font-bold text-black mb-1">{job.title}</h3>
                          <p className="text-gray-600 mb-2">{job.companyName || 'Company'}</p>
                          <div className="flex items-center text-sm text-gray-500 mb-4">
                            <span className="mr-3">{job.location}</span>
                            <span className="mr-3">•</span>
                            <span>₹{job.salaryMin}-{job.salaryMax} LPA</span>
                          </div>
                        </div>
                        <div className={`h-12 w-12 ${logoColor} rounded-md flex items-center justify-center`}>
                          <span className="font-bold">{companyInitials}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(job.skills || []).slice(0, 3).map((skill, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">{skill}</span>
                        ))}
                      </div>
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-gray-500">
                          Posted {daysAgo === 0 ? 'today' : `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`}
                        </span>
                        <button
                          onClick={(e) => handleApplyNow(e, job.id)}
                          className="bg-black text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-gray-800 transition-colors"
                        >
                          Apply Now
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
            <div className="text-center mt-10">
              <Link to="/jobs">
                <button className="bg-white text-black border-2 border-black px-6 py-3 font-bold hover:bg-gray-100 transition-colors">
                  View All Jobs
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 sm:px-6 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-12 text-center">Why Choose Job Loop</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "AI-Powered Job Matching",
                  description: "Our advanced AI algorithms match your skills and experience with the perfect job opportunities."
                },
                {
                  title: "Verified Employers",
                  description: "All employers on our platform are verified to ensure you're applying to legitimate opportunities."
                },
                {
                  title: "Career Growth",
                  description: "Access resources, courses, and mentorship to help you grow in your career journey."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 * index, duration: 0.5 }}
                  className="text-center p-6"
                >
                  <div className="bg-yellow-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4">Job Loop</h3>
            <p className="text-gray-400">Find your dream job with our AI-powered job matching platform.</p>
          </div>
          <div>
            <h4 className="font-bold mb-4">Links</h4>
            <ul className="space-y-2">
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/affiliate/signup" className="text-gray-400 hover:text-white transition-colors">Affiliate Program</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4">Contact Us</h4>
            <p className="text-gray-400">Email: admin@jobloop.in</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
          <p>© 2025 Job Loop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CandidateHomePage;
